import React, { useMemo } from "react";

import { teamBadgeUrl, mean, totalValue, gameweekSortKey, divergingHeatScale } from "./teamUtils";
import SortHeader, { TEAMS_TOOLTIP_ID } from "./SortHeader";

const formatValue = (metric, value) =>
    metric === "goals" ? value.toFixed(1) : `${Math.round(value * 100)}%`;

// Diverging colours around the average of the given values: blue better, red worse
const divergingScale = (values) => {
    if (values.length === 0) return () => ({});
    const average = mean(values);
    const scale = divergingHeatScale(values.map(v => v - average));
    return (value) => scale(value - average);
};

// The teams table, one row per team in the current sort order, with one or both column groups:
// - strength: rank, attack and defence (expected goals for and against an average team)
// - games: a total over the upcoming gameweeks, then one column per gameweek, with either the
//   team's expected goals or its clean sheet chance (`metric`: "goals" / "cs"); the opponent of a
//   game is in its tooltip
// The team name column is shared; with `showTeam` false it's left out (for a table that sits next
// to another one with the same rows).
const PointsPredictionTeamsTable = ({ rows, gameweeks, metric, showStrength, showGames, showTeam = true, sort, onSort }) => {
    const range = gameweeks.length ? `GW${gameweeks[0]}–${gameweeks[gameweeks.length - 1]}` : "";
    const totalLabel = metric === "goals" ? `Expected goals, ${range}` : `Expected clean sheets, ${range}`;

    const styles = useMemo(() => {
        const avgFor = mean(rows.map(t => t.xg_for));
        const avgAgainst = mean(rows.map(t => t.xg_against));
        // Attack: above average is good. Defence: below average conceded is good. One shared
        // scale for attack and defence so the two columns can be compared by eye.
        const attDeviation = t => t.xg_for - avgFor;
        const defDeviation = t => avgAgainst - t.xg_against;
        const strengthScale = divergingHeatScale([...rows.map(attDeviation), ...rows.map(defDeviation)]);
        return {
            att: t => strengthScale(attDeviation(t)),
            def: t => strengthScale(defDeviation(t)),
            game: divergingScale(rows.flatMap(r => r.byGameweek.flat().map(f => f[metric]))),
            total: divergingScale(rows.map(r => totalValue(r, metric))),
        };
    }, [rows, metric]);

    // Widths are CSS variables (see .teams-table), so they can change with the screen size. The
    // total column is a gap and a pixel wider, for the divider between it and the gameweeks.
    const columns = {
        gridTemplateColumns: [
            showStrength && "var(--ts-rank-width)",
            showTeam && "minmax(0, 1fr)",
            showStrength && "var(--ts-cell-width) var(--ts-cell-width)",
            showStrength && showGames && "var(--tt-group-gap)",
            showGames && `calc(var(--ft-cell-width) + var(--ft-column-gap) + 1px) repeat(${gameweeks.length}, var(--ft-gw-width))`,
        ].filter(Boolean).join(" "),
    };
    const groupGap = showStrength && showGames && <div aria-hidden="true"/>;

    // Grid lines where the column groups start, for their labels above the column headers
    const strengthStart = 1 + (showStrength ? 1 : 0) + (showTeam ? 1 : 0);
    const gamesStart = strengthStart + (showStrength ? 2 : 0) + (showStrength && showGames ? 1 : 0);
    const gamesLabel = metric === "goals" ? "Goals" : "Clean sheets";

    if (rows.length === 0) {
        return <div className="teams-empty">Team data is not available yet.</div>;
    }

    return (
        <div className={`teams-table ${showGames ? "with-games" : ""}`} role="table"
             aria-label={[showStrength && "Team strength", showGames && `Upcoming games: ${gamesLabel.toLowerCase()}`]
                 .filter(Boolean).join(", ")}>
            {/* What each group of columns shows; the table's label covers it for screen readers */}
            <div className="teams-table-row teams-group-header" style={columns} aria-hidden="true">
                {showStrength && (
                    <div className="teams-group-label" style={{gridColumn: `${strengthStart} / span 2`}}>Strength</div>
                )}
                {showGames && (
                    <div className="teams-group-label" style={{gridColumn: `${gamesStart} / -1`}}>{gamesLabel}</div>
                )}
            </div>
            <div className="team-strength-row team-strength-header teams-table-row" role="row" style={columns}>
                {showStrength && (
                    <SortHeader className="ts-rank" sortKey="rank" sort={sort} onSort={onSort}
                                tooltip="Sort by team strength">#</SortHeader>
                )}
                {showTeam && (
                    <SortHeader className="ts-team" sortKey="team" sort={sort} onSort={onSort}
                                tooltip="Sort by name">Team</SortHeader>
                )}
                {showStrength && (
                    <>
                        <SortHeader className="ts-cell" sortKey="att" sort={sort} onSort={onSort}
                                    tooltip="Sort by attack">Att</SortHeader>
                        <SortHeader className="ts-cell" sortKey="def" sort={sort} onSort={onSort}
                                    tooltip="Sort by defence">Def</SortHeader>
                    </>
                )}
                {groupGap}
                {showGames && (
                    <>
                        <SortHeader className="ft-gw ft-total-col" sortKey="total" sort={sort} onSort={onSort}
                                    tooltip={`Sort by ${metric === "goals" ? "expected goals" : "expected clean sheets"}, ${range}`}>
                            Total
                        </SortHeader>
                        {gameweeks.map((gw, idx) => (
                            <SortHeader className="ft-gw" key={gw} sortKey={gameweekSortKey(idx)} sort={sort}
                                        onSort={onSort} tooltip={`Sort by GW${gw}`}>GW{gw}</SortHeader>
                        ))}
                    </>
                )}
            </div>
            {rows.map(team => {
                const total = totalValue(team, metric);
                return (
                    <div className="team-strength-row teams-table-row" role="row" style={columns} key={team.team_code}>
                        {showStrength && <div className="ts-rank" role="cell">{team.rank}</div>}
                        {showTeam ? (
                            <div className="ts-team" role="rowheader">
                                <img className="teams-badge" src={teamBadgeUrl(team.team_code)}
                                     height={18} width={18} alt="" loading="lazy"/>
                                <span className="teams-name-full">{team.name}</span>
                                <span className="teams-name-short">{team.team_name}</span>
                            </div>
                        ) : (
                            // Screen readers still get the team, which the table next to it shows
                            <span className="visually-hidden" role="rowheader">{team.name}</span>
                        )}
                        {showStrength && (
                            <>
                                <div className="ts-cell" role="cell">
                                    <div className="teams-heat-cell" style={styles.att(team)}>{team.xg_for.toFixed(1)}</div>
                                </div>
                                <div className="ts-cell" role="cell">
                                    <div className="teams-heat-cell" style={styles.def(team)}>{team.xg_against.toFixed(1)}</div>
                                </div>
                            </>
                        )}
                        {groupGap}
                        {showGames && (
                            <>
                                <div className="ft-gw ft-total-col" role="cell">
                                    <div className="ft-cell" style={styles.total(total)}
                                         data-tooltip-id={TEAMS_TOOLTIP_ID} data-tooltip-content={totalLabel}>
                                        {total.toFixed(1)}
                                    </div>
                                </div>
                                {team.byGameweek.map((games, idx) => (
                                    <div className="ft-gw" role="cell" key={gameweeks[idx]}>
                                        {games.length === 0 ? (
                                            <div className="ft-cell ft-cell-gw ft-blank">–</div>
                                        ) : games.map(f => (
                                            <div className="ft-cell ft-cell-gw" style={styles.game(f[metric])} key={f.id}
                                                 data-tooltip-id={TEAMS_TOOLTIP_ID}
                                                 data-tooltip-content={`${f.opponent}(${f.venue})`}>
                                                {formatValue(metric, f[metric])}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default PointsPredictionTeamsTable;
